/**
 * Iter 45 — Shared fidelity visuals.
 *
 * Small reusable pieces that render the "status_rollup" / per-commitment
 * fidelity data from GET /api/provider/fidelity the same way in every
 * surface (Today, Student workspace, Reports).
 */
import { TrendUp, TrendDown, CheckCircle, Warning, Clock } from "@phosphor-icons/react";

export const FIDELITY_META = {
  met: {
    label: "Met",
    tone: "green",
    icon: CheckCircle,
    verb: "met this period",
    help: "Delivered minutes ≥ committed minutes",
  },
  // FP3-034D — Excused minutes (provider sick/personal/pd, school
  // calendar exceptions, provider blocks) shrink the effective
  // denominator. When delivered hits that adjusted target, the row
  // reads "Met after exclusions" rather than penalizing the provider.
  met_after_exclusions: {
    label: "Met after exclusions",
    tone: "green",
    icon: CheckCircle,
    verb: "met after exclusions",
    help:
      "Delivered minutes met the commitment after excused " +
      "no-service exclusions (sick / personal / PD / school " +
      "closures). Excused minutes do not count against fidelity.",
  },
  // FP3-034D — Provider missed a session for a make-up reason
  // (meeting / conference / other-not-excused). Neutral
  // provider-support language — NOT a violation. Surface make-up
  // need so the provider can plan accordingly.
  needs_makeup: {
    label: "Needs make-up",
    tone: "amber",
    icon: Warning,
    verb: "needs make-up",
    help:
      "Some minutes were missed for a reason that may need " +
      "make-up (meeting / conference / other-not-excused). The " +
      "audit trail is preserved.",
  },
  over: {
    label: "Over",
    tone: "amber",
    icon: TrendUp,
    verb: "over this period",
    help: "Delivered minutes > committed × 1.15 — consider re-scoping",
  },
  behind: {
    label: "Behind",
    tone: "red",
    icon: TrendDown,
    verb: "behind this period",
    help: "Delivered pace < expected with >40% of period elapsed",
  },
  on_track: {
    label: "On track",
    tone: "blue",
    icon: Clock,
    verb: "on track",
    help: "Pacing normally — period still open",
  },
  no_data: {
    label: "No commitment",
    tone: "gray",
    icon: Clock,
    verb: "no commitment",
    help: "This student has no active service obligation",
  },
};

// Tailwind classes keyed by tone so we only maintain one mapping.
const TONE = {
  green: {
    chip: "bg-[#1E5A3F]/10 text-[#1E5A3F] border-[#1E5A3F]/20",
    accent: "text-[#1E5A3F]",
  },
  amber: {
    chip: "bg-[#9C6B2E]/10 text-[#9C6B2E] border-[#9C6B2E]/20",
    accent: "text-[#9C6B2E]",
  },
  red: {
    chip: "bg-[#B85C5C]/10 text-[#B85C5C] border-[#B85C5C]/25",
    accent: "text-[#B85C5C]",
  },
  blue: {
    chip: "bg-[#4A6B8C]/10 text-[#4A6B8C] border-[#4A6B8C]/20",
    accent: "text-[#4A6B8C]",
  },
  gray: {
    chip: "bg-[#9B9890]/15 text-[#6B7280] border-[#E5E3DB]",
    accent: "text-[#6B7280]",
  },
};

export function FidelityChip({ status, size = "sm", showIcon = true, testid }) {
  const meta = FIDELITY_META[status] || FIDELITY_META.on_track;
  const Icon = meta.icon;
  const t = TONE[meta.tone];
  const sz = size === "xs"
    ? "text-[10px] px-1.5 py-0 h-[18px]"
    : size === "sm"
      ? "text-[10.5px] px-2 py-0.5"
      : "text-[12px] px-2.5 py-1";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${t.chip} ${sz}`}
      title={meta.help}
      data-testid={testid || `fidelity-chip-${status}`}
    >
      {showIcon && <Icon size={size === "xs" ? 10 : 11} weight="duotone" />}
      {meta.label}
    </span>
  );
}

export function FidelityProgressBar({ delivered, committed, cadence = "week", testid }) {
  const target = Math.max(1, committed || 1);
  const delivered_ = Math.max(0, delivered || 0);
  const pct = Math.min(150, (delivered_ / target) * 100);
  const isOver = delivered_ > target * 1.15;
  const isMet = delivered_ >= target && !isOver;
  const barColor = isOver
    ? "bg-[#9C6B2E]"
    : isMet
      ? "bg-[#1E5A3F]"
      : "bg-[#4A6B8C]";
  const overflowColor = isOver ? "bg-[#9C6B2E]/40" : null;
  return (
    <div className="w-full" data-testid={testid}>
      <div className="flex items-end justify-between gap-2 mb-0.5">
        <span className="text-[10.5px] text-[#6B7280]">
          <span className="font-semibold tabular-nums text-[#1F2937]">{delivered_}</span>
          <span className="mx-0.5">/</span>
          <span className="tabular-nums">{target}</span>
          <span className="ml-1 text-[#9B9890]">min this {cadence}</span>
        </span>
        <span className={`text-[10px] tabular-nums font-semibold ${TONE[isOver ? "amber" : isMet ? "green" : "blue"].accent}`}>
          {Math.round(pct)}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[#ECEAE4] overflow-hidden relative">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
        {isOver && overflowColor && (
          <div
            className={`absolute top-0 h-full ${overflowColor}`}
            style={{ left: "100%", width: `${Math.min(50, pct - 100)}%`, transform: "translateX(-100%)" }}
          />
        )}
      </div>
    </div>
  );
}

export function fidelityBannerCopy(status, delivered, committed, cadence) {
  const diff = Math.abs((delivered || 0) - (committed || 0));
  switch (status) {
    case "met":
      return `Committed ${committed} min/${cadence} — already delivered ${delivered}.`;
    case "over":
      return `Over committed minutes by ${diff} min this ${cadence}. Consider re-scoping.`;
    case "behind":
      return `Behind pace — delivered ${delivered} of ${committed} min this ${cadence}.`;
    case "on_track":
      return `On track — delivered ${delivered} of ${committed} min this ${cadence} so far.`;
    default:
      return "No active service commitment on file.";
  }
}
